import { Fragment, useState } from 'react';
import {
    Box,
    CircularProgress,
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import { css } from '@emotion/react';

import { IGroupLayer } from '../models/interfaces/IGroupLayer';
import { useGetGroupLayers } from '../api/useGroupLayerController';

const treeViewStyle = css({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: 8,
    '& .tree-header': {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    '& .tree-container': {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    '& .tree-loading': {
        display: 'flex',
        justifyContent: 'center',
        padding: 32,
    },
    '& .tree-message': {
        padding: '24px 16px',
    },
});

interface IGroupLayerNodeProps {
    group: IGroupLayer;
    dataSourceId: number;
    depth: number;
    value: IGroupLayer | null;
    onChange: (group: IGroupLayer | null) => void;
}

function GroupLayerNode({ group, dataSourceId, depth, value, onChange }: IGroupLayerNodeProps) {
    const [expanded, setExpanded] = useState(false);
    const { data: children, isLoading } = useGetGroupLayers(dataSourceId, group.Id, expanded);

    const isSelected = value?.Id === group.Id;
    const isLeaf = children !== undefined && children.length === 0;

    function handleExpandClick(e: React.MouseEvent) {
        e.stopPropagation();
        if (!isLeaf) {
            setExpanded((prev) => !prev);
        }
    }

    function handleSelect() {
        onChange(isSelected ? null : group);
    }

    return (
        <Fragment>
            <ListItemButton
                onClick={handleSelect}
                selected={isSelected}
                sx={{ pl: 1 + depth * 2, py: 0.5 }}
            >
                <ListItemIcon sx={{ minWidth: 24 }} onClick={handleExpandClick}>
                    {isLoading ? (
                        <CircularProgress size={16} />
                    ) : isLeaf ? (
                        <Box sx={{ width: 20 }} />
                    ) : expanded ? (
                        <IndeterminateCheckBoxOutlinedIcon fontSize='small' sx={{ color: '#9e9e9e' }} />
                    ) : (
                        <AddBoxOutlinedIcon fontSize='small' sx={{ color: '#9e9e9e' }} />
                    )}
                </ListItemIcon>
                <ListItemText
                    primary={group.Title}
                    primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isSelected ? 700 : 400,
                        color: isSelected ? '#1565c0' : 'inherit',
                    }}
                />
                {isSelected && <CheckIcon fontSize='small' sx={{ color: '#1565c0', flexShrink: 0 }} />}
            </ListItemButton>

            <Collapse in={expanded && children !== undefined} unmountOnExit>
                <List disablePadding>
                    {children?.map((child) => (
                        <GroupLayerNode
                            key={child.Id}
                            group={child}
                            dataSourceId={dataSourceId}
                            depth={depth + 1}
                            value={value}
                            onChange={onChange}
                        />
                    ))}
                </List>
            </Collapse>
        </Fragment>
    );
}

interface IGroupLayerTreeViewProps {
    dataSourceId: number | null;
    value: IGroupLayer | null;
    onChange: (group: IGroupLayer | null) => void;
    label?: string;
}

export function GroupLayerTreeView({ dataSourceId, value, onChange, label = 'Group Layer' }: IGroupLayerTreeViewProps) {
    const { data: roots, isLoading } = useGetGroupLayers(
        dataSourceId ?? undefined,
        undefined,
        !!dataSourceId,
    );

    return (
        <Box css={treeViewStyle}>
            <Box className='tree-header'>
                <Typography variant='caption' color='text.secondary'>
                    {label}
                </Typography>
                {value !== null && (
                    <Fragment>
                        <CheckCircleOutlineIcon sx={{ fontSize: 13, color: '#1565c0' }} />
                        <Typography variant='caption' sx={{ color: '#1565c0', fontWeight: 600 }}>
                            {value.Title}
                        </Typography>
                    </Fragment>
                )}
            </Box>

            <Paper variant='outlined' className='tree-container'>
                {!dataSourceId && (
                    <Box className='tree-message'>
                        <Typography variant='body2' color='text.secondary'>
                            Select a Data Source to load groups.
                        </Typography>
                    </Box>
                )}

                {dataSourceId && isLoading && (
                    <Box className='tree-loading'>
                        <CircularProgress />
                    </Box>
                )}

                {dataSourceId && !isLoading && roots !== undefined && roots.length === 0 && (
                    <Box className='tree-message'>
                        <Typography variant='body2' color='text.secondary'>
                            No groups found.
                        </Typography>
                    </Box>
                )}

                {dataSourceId && !isLoading && roots && roots.length > 0 && (
                    <List dense disablePadding>
                        {roots.map((group) => (
                            <GroupLayerNode
                                key={group.Id}
                                group={group}
                                dataSourceId={dataSourceId}
                                depth={0}
                                value={value}
                                onChange={onChange}
                            />
                        ))}
                    </List>
                )}
            </Paper>
        </Box>
    );
}
